import ApiService from "@/core/services/ApiService";
import {
  isUserSettingKey,
  type UserSettingKey,
} from "@/services/UserSettingsHotkeys";

type ApiEnvelope = {
  type?: string;
  message?: unknown;
  messages?: unknown;
  response?: unknown;
};

type SettingPayload = {
  key?: unknown;
  value?: unknown;
};

export type UserSettingsLoadResult =
  | { success: true; settings: Partial<Record<UserSettingKey, string>> }
  | { success: false; message: string };

export type UserSettingSaveResult =
  | { success: true; setting: { key: UserSettingKey; value: string } }
  | { success: false; message: string };

class UserSettingsService {
  async load(): Promise<UserSettingsLoadResult> {
    try {
      const response = await ApiService.httpRequest.get<ApiEnvelope>(
        "api/settings/user",
        { validateStatus: () => true }
      );
      const payload = asEnvelope(response.data);

      if (!isSuccess(response.status, payload)) {
        return {
          success: false,
          message: apiMessage(
            payload,
            "Не удалось загрузить настройки хоткеев."
          ),
        };
      }

      const responseData = asRecord(payload.response);
      const rawSettings = asRecord(responseData.settings);
      const settings: Partial<Record<UserSettingKey, string>> = {};

      for (const [key, value] of Object.entries(rawSettings)) {
        if (isUserSettingKey(key) && typeof value === "string") {
          settings[key] = value;
        }
      }

      return { success: true, settings };
    } catch {
      return {
        success: false,
        message:
          "Не удалось связаться с сервером. Проверьте подключение и повторите попытку.",
      };
    }
  }

  async save(
    key: UserSettingKey,
    value: string
  ): Promise<UserSettingSaveResult> {
    try {
      const response = await ApiService.httpRequest.put<ApiEnvelope>(
        "api/settings/user",
        { key, value },
        { validateStatus: () => true }
      );
      const payload = asEnvelope(response.data);

      if (!isSuccess(response.status, payload)) {
        return {
          success: false,
          message: apiMessage(payload, "Не удалось сохранить сочетание."),
        };
      }

      const setting = asRecord(
        asRecord(payload.response).setting
      ) as SettingPayload;
      if (
        typeof setting.key !== "string" ||
        !isUserSettingKey(setting.key) ||
        typeof setting.value !== "string"
      ) {
        return {
          success: false,
          message: "Сервер вернул неполный ответ. Повторите попытку.",
        };
      }

      return {
        success: true,
        setting: { key: setting.key, value: setting.value },
      };
    } catch {
      return {
        success: false,
        message:
          "Не удалось связаться с сервером. Проверьте подключение и повторите попытку.",
      };
    }
  }
}

function asEnvelope(value: unknown): ApiEnvelope {
  return asRecord(value) as ApiEnvelope;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object"
    ? (value as Record<string, unknown>)
    : {};
}

function isSuccess(status: number, payload: ApiEnvelope): boolean {
  return status >= 200 && status < 300 && payload.type === "success";
}

function apiMessage(payload: ApiEnvelope, fallback: string): string {
  const directMessage = firstMessage(payload.message);
  if (directMessage) return directMessage;

  const messages = asRecord(payload.messages);
  for (const message of Object.values(messages)) {
    const text = firstMessage(message);
    if (text) return text;
  }

  return fallback;
}

function firstMessage(value: unknown): string {
  if (typeof value === "string" && value.trim()) return value;
  if (Array.isArray(value)) {
    const message = value.find(
      (item): item is string => typeof item === "string" && Boolean(item.trim())
    );
    return message ?? "";
  }
  return "";
}

export default new UserSettingsService();
